'use client';

import React, {
  cloneElement,
  createContext,
  useContext,
  type ReactElement,
  type ReactNode,
} from 'react';
import { CountBadge } from './count-badge';
import { Popover } from './popover';
import { StateKit } from './state-kit';
import {
  attentionCountLabel,
  compareAttention,
  groupAttentionItems,
  isAttentionLimited,
  type AttentionItem,
} from './attention-model';

export type CountedAttentionChrome = {
  customise?: ReactNode;
  wrap?: ((parts: CountedAttentionParts) => ReactNode) | ReactElement<CountedAttentionWrapProps>;
  link?: ReactElement<CountedAttentionLinkProps>;
  /** Preserve ReactNode slot rows while sharing the counted queue's ordering. */
  renderRows?: (visible: readonly AttentionItem[]) => ReactNode;
};

type CountedAttentionParts = {
  title: string;
  heading: ReactNode;
  summary: ReactNode;
  rows: ReactNode;
  footer: ReactNode;
  collapsedContent: ReactNode;
};
type CountedAttentionWrapProps = {
  title: string;
  summary: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  collapsedContent?: ReactNode;
};
type CountedAttentionLinkProps = {
  href: string;
  children: ReactNode;
  className?: string;
  scroll?: boolean;
};

const AttentionLinkContext = createContext<CountedAttentionChrome['link']>(undefined);

/** True while a work-home layout is still resolving; disclosure controls wait, failures and Retry stay visible. */
export const LayoutPendingContext = createContext(false);

// Read where the element renders (inside a pending section), not where the queue builds it.
function HideWhileLayoutPending({ children }: { children: ReactNode }) {
  return useContext(LayoutPendingContext) ? null : <>{children}</>;
}
function ActionLink({
  href,
  children,
  className,
  scroll,
}: {
  href?: string;
  children: ReactNode;
  className?: string;
  scroll?: boolean;
}) {
  const link = useContext(AttentionLinkContext);
  if (!href) return <span className={className}>{children}</span>;
  return link ? (
    cloneElement(link, {
      href,
      scroll: scroll ?? (/[?&](resolve|queue)=/.test(href) ? false : undefined),
      className,
      children,
    })
  ) : (
    <a href={href} className={className}>
      {children}
    </a>
  );
}

const severityStyle = {
  'legal-block': { label: 'Legal', tone: 'text-status-danger-fg' },
  deadline: { label: 'Due', tone: 'text-status-warning-fg' },
  integrity: { label: 'Check', tone: 'text-status-information-fg' },
  warn: { label: 'At risk', tone: 'text-status-warning-fg' },
  routine: { label: 'Follow up', tone: 'text-text-muted' },
};

export function CountedAttentionRow({
  item,
  actionLabel,
  primary = false,
  link,
}: {
  item: AttentionItem;
  actionLabel: string;
  primary?: boolean;
  link?: CountedAttentionChrome['link'];
}) {
  const inheritedLink = useContext(AttentionLinkContext);
  if (!actionLabel.trim() || actionLabel.trim().toLowerCase() === 'review')
    throw new Error('CountedAttentionRow requires a specific actionLabel');
  const severity =
    item.count === null
      ? { label: item.protected ? 'Protected' : 'Check', tone: 'text-text-muted' }
      : severityStyle[item.severity];
  return (
    <AttentionLinkContext.Provider value={link ?? inheritedLink}>
      <li
        className={`grid min-h-[52px] grid-cols-1 items-center gap-x-4 gap-y-1 sm:grid-cols-[minmax(12rem,1fr)_auto] px-4 py-2 text-body ${primary ? (item.severity === 'legal-block' ? 'bg-status-danger-bg/40' : 'bg-surface-soft') : ''}`}
      >
        <div className="min-w-0">
          <h3 className="truncate font-sans text-body font-semibold text-text-heading">
            {item.sentence ?? (
              <>
                {isAttentionLimited(item) && item.count !== null ? 'At least ' : ''}
                {item.count ?? '—'} {item.title}
              </>
            )}
          </h3>
          <p className="break-words text-caption text-text-muted">
            <span className={`mr-2 inline-flex items-center gap-1 ${severity.tone}`}>
              <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-current" />
              {item.protected ? 'Protected' : severity.label}
            </span>
            {isAttentionLimited(item) ? 'Limited to loaded records · ' : ''}
            {item.count !== null && item.count <= 3 && item.chips?.length
              ? `${item.chips.join(', ')} · `
              : ''}
            {item.reason}
          </p>
        </div>
        {item.action.control ? (
          <span className="inline-flex min-h-11 items-center [&>a]:min-h-11 [&>button]:min-h-11">
            {item.action.control}
          </span>
        ) : (
          <ActionLink
            href={item.action.href}
            className={`inline-flex min-h-11 max-w-36 items-center justify-center rounded-lg border px-3 py-1 text-caption font-semibold focus-visible:ring-2 sm:max-w-none ${primary ? 'border-statura-ink-dark bg-statura-ink-dark text-primary-foreground' : 'border-border bg-surface-sheet text-text-heading'}`}
          >
            {actionLabel}
          </ActionLink>
        )}
      </li>
    </AttentionLinkContext.Provider>
  );
}
export function CountedAttentionQueue({
  items,
  title = 'Needs action',
  limit = 3,
  retry,
  status = 'ready',
  onRetry,
  chrome,
  showAllHref,
  emptyAlsoOpenLabel,
}: {
  items: AttentionItem[];
  title?: string;
  limit?: number;
  status?: 'ready' | 'loading' | 'failed';
  onRetry?: () => void;
  chrome?: CountedAttentionChrome;
  /** Caller owns retry of its existing readers. A client reload control preserves server reader inputs. */
  retry?: ReactNode;
  showAllHref?: string;
  /** Keep the shared summary line when no outstanding queues are folded. */
  emptyAlsoOpenLabel?: string;
}) {
  const protectedItems = items
    .filter((item) => item.protected && item.count !== 0)
    .sort(compareAttention);
  const visible = [
    ...groupAttentionItems(items),
    ...protectedItems.filter((item) => item.count === null),
  ].sort(compareAttention);
  const clear = items.filter((item) => item.count === 0 && !isAttentionLimited(item));
  const unknown = items.filter((item) => item.count === null);
  const failures = unknown.filter(
    (item) => !item.unavailableReason || item.unavailableReason === 'failed',
  );
  const loading = unknown.filter((item) => item.unavailableReason === 'loading');
  const unsupported = unknown.filter((item) => item.unavailableReason === 'unsupported');
  const limited = items.filter(isAttentionLimited);
  const summaryStatus =
    !visible.length && !unknown.length && !limited.length && items.length
      ? 'All clear'
      : clear.length
        ? `${clear.length} clear`
        : '';
  const statusParts = [
    summaryStatus,
    failures.length ? `${failures.length} couldn't load` : '',
    loading.length ? 'Checking…' : '',
    ...(['scope', 'view'] as const).map((context) => {
      const count = unsupported.filter(
        (item) => (item.unavailableContext ?? 'scope') === context,
      ).length;
      return count
        ? `${count} count${count === 1 ? '' : 's'} not available in this ${context}`
        : '';
    }),
  ].filter(Boolean);
  // Unavailable and capped counts must be visible on the surface, not only in the ⓘ disclosure.
  const partialStatus = [
    failures.length
      ? `${failures.length} count${failures.length === 1 ? '' : 's'} couldn't load`
      : '',
    loading.length ? 'Checking counts…' : '',
    ...(['scope', 'view'] as const).map((context) => {
      const count = unsupported.filter(
        (item) => (item.unavailableContext ?? 'scope') === context,
      ).length;
      return count
        ? `${count} count${count === 1 ? '' : 's'} not available in this ${context}`
        : '';
    }),
    limited.length ? `${limited.length} capped at loaded records` : '',
  ].filter(Boolean);
  const cap = Number.isFinite(limit) ? Math.min(5, Math.max(1, Math.floor(limit))) : 3;
  const firstRows = visible.filter((item) => !item.volume || item.protected).slice(0, cap);
  const overflowProtected = visible.filter((item) => item.protected && !firstRows.includes(item));
  const remaining = visible.filter((item) => !item.protected && !firstRows.includes(item));
  const footer = (
    <div className="relative flex min-w-0 flex-wrap items-center gap-x-2 text-caption text-text-muted">
      <span
        className="min-w-0 flex-1 truncate"
        title={remaining
          .map(
            (item) =>
              item.sentence ??
              `${isAttentionLimited(item) && item.count !== null ? 'at least ' : ''}${item.count} ${item.title}`,
          )
          .join(' · ')}
      >
        {remaining.length
          ? `Also open: ${remaining.map((item) => item.sentence ?? `${isAttentionLimited(item) && item.count !== null ? 'at least ' : ''}${item.count ?? '—'} ${item.title.toLowerCase()}`).join(' · ')} · `
          : emptyAlsoOpenLabel
            ? `Also open: ${emptyAlsoOpenLabel} · `
            : ''}
      </span>
      <span className="shrink-0">
        {summaryStatus === 'All clear'
          ? summaryStatus
          : clear.length || !partialStatus.length
            ? `${clear.length} checks clear`
            : ''}
      </span>
      {partialStatus.length ? (
        <span className="shrink-0 font-semibold text-status-warning-fg" data-attention-partial>
          {partialStatus.join(' · ')}
        </span>
      ) : null}
      <HideWhileLayoutPending>
        <Popover
          role="region"
          ariaLabel="Check availability"
          align="end"
          focusInteractiveOnOpen
          renderClosedContent
          trigger={
            <button
              type="button"
              aria-label="Check availability"
              className="inline-flex min-h-11 min-w-11 shrink-0 items-center justify-center rounded focus-visible:ring-2"
            >
              ⓘ
            </button>
          }
        >
          <div className="max-h-80 w-72 max-w-full overflow-auto">
            {statusParts.length ? <p>{statusParts.join(' · ')}</p> : null}
            {limited.length ? <p>{limited.length} limited to loaded records</p> : null}
            <ul className="space-y-2">
              {unknown.map((item) => (
                <li key={item.id}>
                  <ActionLink href={item.action.href}>{item.title}</ActionLink>:{' '}
                  {attentionCountLabel(item)}
                </li>
              ))}
              {visible
                .filter((item) => item.detail || item.breakdown)
                .map((item) => (
                  <li key={item.id}>
                    <p>{item.title} · Individual checks</p>
                    {item.detail}
                    {item.breakdown?.map((check) => (
                      <p key={check.id}>
                        <ActionLink href={check.action.href}>
                          {check.action.label} · {check.title} ·{' '}
                          {isAttentionLimited(check) ? 'at least ' : ''}
                          {check.count}
                        </ActionLink>{' '}
                        · {check.reason}
                      </p>
                    ))}
                  </li>
                ))}
            </ul>
          </div>
        </Popover>
      </HideWhileLayoutPending>
      {failures.length
        ? (retry ?? (
            <button
              type="button"
              onClick={onRetry ?? (() => window.location.reload())}
              className="inline-flex min-h-11 shrink-0 items-center rounded font-semibold text-primary focus-visible:ring-2"
            >
              Retry
            </button>
          ))
        : null}
    </div>
  );
  const showAll =
    status === 'ready' && showAllHref ? (
      <ActionLink
        href={showAllHref}
        scroll={false}
        className="inline-flex min-h-11 items-center text-caption font-semibold text-primary"
      >
        Show all {visible.length}
      </ActionLink>
    ) : null;
  const sectionActions = (
    <span className="flex items-center gap-4">
      {chrome?.customise}
      {showAll}
    </span>
  );
  const rows = chrome?.renderRows ? (
    chrome.renderRows(visible)
  ) : (
    <>
      {firstRows.length ? (
        <ul
          aria-label={title}
          className="overflow-hidden rounded-lg border border-border bg-surface-sheet divide-y divide-border"
        >
          {firstRows.map((item, index) => (
            <CountedAttentionRow
              key={item.id}
              item={item}
              actionLabel={item.action.label}
              primary={index === 0 && (Boolean(item.protected) || item.severity === 'legal-block')}
            />
          ))}
        </ul>
      ) : null}
      {overflowProtected.length ? (
        <ul aria-label="Additional protected work" className="text-caption text-text-muted">
          {overflowProtected.map((item) => (
            <li key={item.id}>
              <ActionLink
                href={item.action.href}
                className="inline-flex min-h-11 items-center font-semibold"
              >
                {item.sentence ??
                  `${isAttentionLimited(item) && item.count !== null ? 'At least ' : ''}${item.count ?? '—'} ${item.title}`}{' '}
                · {item.action.label} · Protected
              </ActionLink>
            </li>
          ))}
        </ul>
      ) : null}
      {remaining.length && !showAllHref ? (
        <details>
          <summary className="min-h-11 cursor-pointer content-center text-caption focus-visible:ring-2">
            Show all {visible.length}
          </summary>
          <ul className="divide-y divide-border">
            {remaining.map((item) => (
              <CountedAttentionRow key={item.id} item={item} actionLabel={item.action.label} />
            ))}
          </ul>
        </details>
      ) : null}
    </>
  );
  const compact = protectedItems.length ? (
    <ul aria-label="Protected urgent work" className="text-body text-text-muted">
      {protectedItems.map((item) => (
        <li key={item.id}>
          <ActionLink href={item.action.href} className="inline-flex min-h-11 items-center gap-2">
            {item.title}: {isAttentionLimited(item) && item.count !== null ? 'At least ' : ''}
            {item.count ?? '—'} · {item.action.label} · Protected
          </ActionLink>
        </li>
      ))}
    </ul>
  ) : null;
  const content =
    status === 'loading' ? (
      <StateKit state="loading" />
    ) : status === 'failed' ? (
      <StateKit state="failed" onRetry={onRetry ?? (() => window.location.reload())} />
    ) : (
      rows
    );
  const count = status !== 'ready' || unknown.length || limited.length ? null : visible.length;
  const heading = (
    <span className="inline-flex items-center gap-2">
      {title}
      {count !== null ? (
        <CountBadge count={count} label="need action" aria-label={`${count} need action`} />
      ) : null}
    </span>
  );
  const parts = {
    title,
    heading,
    summary: (
      <>
        {count !== null ? (
          <CountBadge count={count} label="need action" aria-label={`${count} need action`} />
        ) : null}
        {sectionActions}
      </>
    ),
    rows: content,
    footer: status === 'ready' ? footer : null,
    collapsedContent: status === 'ready' ? compact : null,
  };
  return (
    <AttentionLinkContext.Provider value={chrome?.link}>
      {chrome?.wrap ? (
        typeof chrome.wrap === 'function' ? (
          chrome.wrap(parts)
        ) : (
          cloneElement(chrome.wrap, {
            title,
            summary: parts.summary,
            children: content,
            footer: parts.footer,
            collapsedContent: parts.collapsedContent,
          })
        )
      ) : (
        <section aria-label={title} className="min-w-0 space-y-2">
          <div className="flex min-w-0 flex-wrap items-start gap-x-4 gap-y-3">
            <h2 className="min-w-0 flex-[1_1_16rem] break-normal [overflow-wrap:break-word] font-sans text-section-title leading-6">
              {heading}
            </h2>
            <div className="ml-auto flex min-w-0 max-w-full flex-[0_1_auto] flex-wrap items-center justify-start gap-2">
              {sectionActions}
            </div>
          </div>
          {content}
          {status === 'ready' ? footer : null}
        </section>
      )}
    </AttentionLinkContext.Provider>
  );
}
